import { useState, useMemo } from "react";
import { showToast } from "../../../../core/utils/alert";
import { useCourse } from "../../../configuration/course/hooks/useCourse";
import { useSession } from "../../../configuration/session/hooks/useSession";
import { useGetStudentsEligibleForBooksQuery } from "../../../peoples/students/api/studentApi";
import { useBulkSaveBookRequestMutation } from "../api/bookRequestApi";
import { getApiErrorMessage } from "../../../../core/utils/errorHandler";

export const useBulkBookRequest = () => {
  // Filter States
  const [selectedSession, setSelectedSession] = useState<string>("");
  const [selectedCourse, setSelectedCourse] = useState<string>("");
  const [selectedStage, setSelectedStage] = useState<string>("");
  
  // Table States
  const [selectedStudentKeys, setSelectedStudentKeys] = useState<React.Key[]>([]);
  const [search, setSearch] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [isRequestingBulk, setIsRequestingBulk] = useState<boolean>(false);

  const [bulkSaveBookRequest] = useBulkSaveBookRequestMutation();

  // Get configuration data
  const { getCourseState } = useCourse({ published: true });
  const { getSessionState } = useSession();
  const { courses, isLoading: isLoadingCourses } = getCourseState();
  const { sessions, isLoading: isLoadingSessions } = getSessionState();

  // Get student data
  const {
    data: eligibleStudentsData,
    isLoading: isLoadingStudents,
    isFetching: isFetchingStudents,
    refetch: refetchStudents,
  } = useGetStudentsEligibleForBooksQuery(
    {
      page: pageNumber,
      size: recordsPerPage,
      session_id: selectedSession ? parseInt(selectedSession) : undefined,
      course_id: selectedCourse ? parseInt(selectedCourse) : undefined,
      stage_id: selectedStage ? parseInt(selectedStage) : undefined,
    },
    {
      skip: !selectedSession || !selectedCourse || !selectedStage, // Only fetch when filters are selected
    }
  );

  const students = eligibleStudentsData?.data?.items || [];
  const paginationData = eligibleStudentsData?.data || { totalItems: 0 };

  // Helper to get stage options based on selected course
  const getStageOptions = (): any[] => {
    if (!selectedCourse) return [];
    const course = courses.find((c: any) => c.id.toString() === selectedCourse);
    return course?.courseStages || [];
  };

  const handleSelectionChange = (selectedKeys: React.Key[]) => {
    setSelectedStudentKeys(selectedKeys);
  };

  const handleSearchChange = (text: string) => {
    setSearch(text);
    setPageNumber(1);
  };

  const handlePageChange = (page: number, pageSize?: number) => {
    setPageNumber(page);
    if (pageSize) {
      setRecordsPerPage(pageSize);
    }
  };

  const handleRequestAll = async () => {
    if (
      !selectedCourse ||
      !selectedStage ||
      !selectedSession ||
      selectedStudentKeys.length === 0
    ) {
      showToast({
        title: "Error",
        text: "Please select course, level, session and students to request books for",
        icon: "error",
        timer: 5000,
      });
      return;
    }
    
    setIsRequestingBulk(true);
    
    try {
      const payload = {
        book_requests: selectedStudentKeys.map((studentId) => ({
          student_id: Number(studentId),
          course_id: Number(selectedCourse),
          stage_id: Number(selectedStage) + 1
        }))
      };

      await bulkSaveBookRequest(payload).unwrap();

      showToast({
        title: "Success",
        text: "Books requested successfully for selected students",
        icon: "success",
      });
      setSelectedStudentKeys([]);
      refetchStudents();
    } catch (error: any) {
      showToast({
        title: "Error",
        text: getApiErrorMessage(error) || "Failed to submit bulk book requests",
        icon: "error",
      });
    } finally {
      setIsRequestingBulk(false);
    }
  };

  const handleRequestIndividual = async (record: any) => {
     if (!selectedCourse || !selectedStage || !selectedSession) {
      showToast({
        title: "Error",
        text: "Please select course, level, and session first",
        icon: "error",
      });
      return;
    }
    
    try {
      const payload = {
        book_requests: [
          {
            student_id: Number(record.id),
            course_id: Number(selectedCourse),
            stage_id: Number(selectedStage) + 1
          }
        ]
      };

      await bulkSaveBookRequest(payload).unwrap();

      showToast({
        title: "Success",
        text: `Requested book for ${record.name}`,
        icon: "success",
      });
      refetchStudents();
    } catch (error: any) {
      showToast({
        title: "Error",
        text: getApiErrorMessage(error) || "Failed to request book",
        icon: "error",
      });
    }
  };

  // Transform student data for the table
  const transformedStudents = useMemo(() => {
    return students.map((student: any) => {
      // Check if the student has any ACTIVE (not rejected) book request matching the currently selected course and stage
      const hasActiveRequest = student.bookRequests?.some(
        (request: any) => 
          request.course_id === parseInt(selectedCourse) && 
          request.stage_id === parseInt(selectedStage) + 1 &&
          request.status !== "rejected"
      );

      return {
        id: student.id,
        studentId: student.profile?.studentid || "",
        name: student.name || "",
        courseName: student.course?.name || "-",
        stageName: student.course?.stage?.name || "-",
        status: student.status,
        hasActiveRequest: !!hasActiveRequest,
      };
    });
  }, [students, selectedCourse, selectedStage]);

  const getCheckboxProps = (record: any) => ({
    name: record.name,
    disabled: record.hasActiveRequest,
  });

  const areAllFiltersSelected = () => {
    return selectedSession !== "" && selectedCourse !== "" && selectedStage !== "";
  };

  return {
    selectedSession,
    setSelectedSession,
    selectedCourse,
    setSelectedCourse,
    selectedStage,
    setSelectedStage,
    selectedStudentKeys,
    search,
    pageNumber,
    recordsPerPage,
    isRequestingBulk,
    sessions,
    isLoadingSessions,
    courses,
    isLoadingCourses,
    isLoadingStudents,
    isFetchingStudents,
    paginationData,
    transformedStudents,
    getStageOptions,
    handleSelectionChange,
    handleSearchChange,
    handlePageChange,
    handleRequestAll,
    handleRequestIndividual,
    getCheckboxProps,
    areAllFiltersSelected,
  };
};
